<script setup>
import { ref, computed } from "vue";
import { useUser } from "@/store/user";
import BlockView from "@/components/common/BlockView.vue";
import RuleCard from "./TelegramNotifications/RuleCard.vue";
import { t } from "@/controllerLK/GlobusLocalization";
import PlusIcon from "@/assets/icons/PlusIcon.vue";

const props = defineProps({
  projectId: {
    type: [String, Number],
    default: null
  },
  isEdit: {
    type: Boolean,
    default: false
  },
  verified: {
    type: Boolean,
    default: false
  },
  rules: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  availableTopics: {
    type: Array,
    default: () => []
  },
  topicSearchQuery: {
    type: String,
    default: ""
  },
  filteredTopics: {
    type: Array,
    default: () => []
  },
  recursivelyVerifying: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  "update:topicSearchQuery",
  "verifySubscription",
  "stopVerification",
  "addRule",
  "deleteRule",
  "toggleImportance",
  "toggleTonality",
  "toggleTopic",
  "toggleAllTopics",
  "setFrequency",
  "setSendTime",
  "setTimezone",
  "setWeekday"
]);

const isNotificationsEnabled = ref(true);
const buttonAutorizeDisabled = ref(false);

// Refs для карточек правил
const ruleCardRefs = ref([]);

const localTopicSearchQuery = computed({
  get: () => props.topicSearchQuery,
  set: (val) => emit("update:topicSearchQuery", val)
});

const setFrequency = (rule, frequency) => {
  emit("setFrequency", rule, frequency);
};

const setSendTime = (rule, time) => {
  emit("setSendTime", rule, time);
};

const setTimezone = (rule, tz, tz_label) => {
  emit("setTimezone", rule, tz, tz_label);
};

const setWeekDay = (rule, weekday) => {
  emit("setWeekday", rule, weekday);
};

const closeAllDropdowns = () => {
  ruleCardRefs.value.forEach((ref) => {
    if (ref?.closeAllDropdowns) {
      ref.closeAllDropdowns();
    }
  });
};

// Обработчик удаления правила
const deliteConfirmed = (ruleId) => {
  emit("deleteRule", ruleId);
};

// Обработчик добавления правила
const handleAddRule = () => {
  emit("addRule");
};

const hasOpenDropdowns = ref(false);

const autorize = () => {
  const userStore = useUser();
  const uid = userStore.user.userId;
  const botName = process.env.VUE_APP_TELEGRAM_BOT_NAME || "globus_reports_dev_bot";
  const url = `https://t.me/${botName}?start=${uid}`;
  console.log(`telegram auth bot: ${url}`);
  buttonAutorizeDisabled.value = true;
  window.open(url, "_blank");
  emit("verifySubscription");
};

const stopVerification = () => {
  emit("stopVerification");
};
</script>

<template>
  <div
    class="telegram-notifications"
    :class="{ 'telegram-notifications--verified' : verified && availableTopics.length }"
  >
    <!-- Лоадер авторизации в ТГ. -->
    <div
      v-if="!verified && recursivelyVerifying"
      class="loading-overlay"
    >
      <span>{{ t('telegram_subscription.waiting_authorization') }}</span>
      <ButtonAccentPrimary
        size="large"
        @click="stopVerification"
      >
        {{ t('telegram_subscription.button.cancel') }}
      </ButtonAccentPrimary>
    </div>

    <!-- Основной контент -->
    <template v-else>
      <!-- Плашки уведомлений -->
      <GlobusMessage
        v-if="!verified"
        :use-cross-icon="false"
      >
        <span>{{ t("telegram_subscription.messages.auth_required.first") }}</span>
        &nbsp;
        <span style="color: #1F9AFF; text-decoration: underline">
          {{ t("telegram_subscription.messages.auth_required.second") }}
        </span>
      </GlobusMessage>

      <GlobusMessage
        v-if="verified && availableTopics.length === 0"
        :use-cross-icon="false"
      >
        {{ t("telegram_subscription.messages.themes_required") }}
      </GlobusMessage>

      <GlobusMessage
        v-if="verified && rules.length === 0 && availableTopics.length > 0"
        :use-cross-icon="false"
      >
        {{ t("telegram_subscription.messages.information") }}
      </GlobusMessage>

      <!-- Секция с правилами отображается только если есть темы -->
      <div
        v-if="verified && isNotificationsEnabled && availableTopics.length > 0"
        class="rules-section scroll"
      >
        <RuleCard
          v-for="rule in rules"
          :key="rule.id"
          :ref="(el) => ruleCardRefs[rule.id] = el"
          v-model:topic-search-query="localTopicSearchQuery"
          :rule="rule"
          :available-topics="availableTopics"
          :filtered-topics="filteredTopics"
          @delete="deliteConfirmed"
          @toggle-topic="(rule, topicId) => emit('toggleTopic', rule, topicId)"
          @toggle-all-topics="(rule) => emit('toggleAllTopics', rule)"
          @toggle-importance="(rule, value) => emit('toggleImportance', rule, value)"
          @toggle-tonality="(rule, value) => emit('toggleTonality', rule, value)"
          @set-weekday="setWeekDay"
          @set-frequency="setFrequency"
          @set-send-time="setSendTime"
          @set-timezone="setTimezone"
        />

        <ButtonNeutralTertiary @click="handleAddRule">
          <template #icon-left>
            <PlusIcon class="add-rule-icon" />
          </template>
          {{ t("telegram_subscription.button.add_rule") }}
        </ButtonNeutralTertiary>
      </div>

      <BlockView
        v-if="hasOpenDropdowns"
        @click="closeAllDropdowns"
      />

      <!-- Кнопка авторизации -->
      <ButtonAccentPrimary
        v-if="!verified"
        size="large"
        @click="autorize"
      >
        {{ t('telegram_subscription.button.authorize') }}
      </ButtonAccentPrimary>
    </template>
  </div>
</template>

<style scoped lang="scss">

.telegram-notifications {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  height: 100%;
}

.rules-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  overflow-y: auto;
  height: 100%;
  padding-right: 5px;
}

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  z-index: 10;
  height: 100%;
  
  span {
    color: var(--gray-gray60);
    font-size: 14px;
    font-weight: 500;
  }
}

.add-rule-icon {
  width: 14px;
  height: 14px;
}

</style>
